'use client'

import { Button } from '@repo/design-system-cf/components/button'
import { IconStudio } from '@repo/design-system-cf/components/icons'
import { Link } from '@repo/web-ui/components/link'
import { buildShowcaseVideos } from '@repo/studio-components-showcase-strip/showcase-strip'
import { ShowcaseGrid } from '@repo/studio-components-showcase-grid/showcase-grid'
import { SeedanceVideoItem } from './seedance-video-item'

const LFS_URL = process.env.NEXT_PUBLIC_LFS_URL ?? ''
const STUDIO_BASE_URL = process.env.NEXT_PUBLIC_STUDIO_CF_URL ?? ''
const AI_VIDEO_GENERATOR_URL = `${STUDIO_BASE_URL}/ai-video-generator?model=seedance_v2_5`

// The widest layout is 8 columns × 2 rows (16 tiles). Combine every unique clip we have —
// the eleven seedance-2-0 videos plus five from the alternative set — so no tile repeats.
const SEEDANCE_FILENAMES = Array.from({ length: 11 }, (_, i) => `${i + 1}.webm`)
const SEEDANCE_VIDEOS = buildShowcaseVideos(
  `${LFS_URL}/studio/video/seedance-2-0`,
  'seedance',
  SEEDANCE_FILENAMES,
)
// The alternative folder uses a flat `video-N.webm` layout, so `buildShowcaseVideos`
// (which inserts a `/videos/` segment) can't build these paths.
const ALTERNATIVE_BASE = `${LFS_URL}/studio/homepage/videos/alternative`
const ALTERNATIVE_VIDEOS = Array.from({ length: 5 }, (_, i) => ({
  id: `seedance-alt-${i + 1}`,
  previewUrl: `${ALTERNATIVE_BASE}/video-${i + 1}-compressed.webm`,
}))
const GRID_VIDEOS = [...SEEDANCE_VIDEOS, ...ALTERNATIVE_VIDEOS]

interface SeedanceShowcaseBlockProps {
  badgeLabel: string
  title: string
  subtitle: string
  ctaLabel: string
  className?: string
  /** Scroll anchor — only the homepage, where the trial bar waits for this block, needs one. */
  id?: string
  /**
   * Label for the tiles' hover scrim. Omit to keep the tiles as they are — only the
   * homepage passes it, and only for the GROW-3266 experiment's variant.
   */
  hoverViewLabel?: string
}

export function SeedanceShowcaseBlock({
  badgeLabel,
  title,
  subtitle,
  ctaLabel,
  className = '',
  id,
  hoverViewLabel,
}: SeedanceShowcaseBlockProps) {
  return (
    <ShowcaseGrid.Root id={id} className={`bg-cf-white ${className}`}>
      <ShowcaseGrid.Header
        icon={<IconStudio className="size-5 text-cf-purple-900" />}
        title={title}
        titleClassName="font-poppins text-xl lg:text-2xl font-bold text-cf-purple-900"
        badge={
          <span className="rounded-full bg-cf-purple-100 px-2.5 py-0.5 text-xs font-semibold text-cf-purple-900">
            {badgeLabel}
          </span>
        }
        subtitle={subtitle}
        subtitleClassName="text-sm text-cf-grey-500"
      />
      <ShowcaseGrid.Body>
        <ShowcaseGrid.Grid>
          {GRID_VIDEOS.map((video, index) => (
            <ShowcaseGrid.Tile key={`${video.id}-${index}`} index={index}>
              <SeedanceVideoItem
                src={video.previewUrl}
                tryItLabel={ctaLabel}
                className="h-full w-full"
                aspectClassName="h-full"
                showDuration
                hoverViewLabel={hoverViewLabel}
              />
            </ShowcaseGrid.Tile>
          ))}
        </ShowcaseGrid.Grid>
        <ShowcaseGrid.Cta scrimClassName="bg-gradient-to-t from-cf-white via-cf-white/85 to-transparent">
          <Button
            variant="primary"
            size="regular"
            asChild
            className="absolute bottom-2 left-1/2 -translate-x-1/2 gap-1 shadow-lg"
          >
            <Link href={AI_VIDEO_GENERATOR_URL} target="_blank" rel="noopener noreferrer">
              <IconStudio className="!size-4" />
              <span>{ctaLabel}</span>
            </Link>
          </Button>
        </ShowcaseGrid.Cta>
      </ShowcaseGrid.Body>
    </ShowcaseGrid.Root>
  )
}
